/**
 * hubCardDescription — the one author of Related-Resources card subheaders.
 *
 * FRIDGE-661 (owner annotation on a live PDP screenshot, 08/31/2026: "all
 * subheadders need variety"). The previous logic was a slug-prefix ternary
 * whose fallback — "Category hub with live prices" — blanketed all eight
 * cards on the screenshotted PDP, and was FALSE for three of them (two
 * guides and a ranked list). The server has always fetched `hub_type`
 * (`lib/supabase/hubs.ts` HUB_SELECT); the client just dropped it.
 *
 * Two rules this module owes the owner:
 *  1. TRUTH — the label must describe what the destination IS. hub_type is
 *     the registry-backed nature; the slug prefix covers the non-hub
 *     destinations (tools, collections, articles).
 *  2. VARIETY — no two adjacent cards may read identically, even when they
 *     share a hub_type. Each type carries two phrasings; the render slot
 *     picks one deterministically (stable across renders — no hydration
 *     drift, no Math.random).
 */

const TYPE_PHRASINGS: Record<string, [string, string]> = {
  // hub_pages.hub_type values, measured live 08/31/2026:
  // filter 15 · brand 14 · specialty 10 · category 9 · feature 6 · guide 5 ·
  // comparison 2
  category: ['Category hub with live prices', 'Every model in the class, priced live'],
  brand: ['Brand lineup with live prices', 'The full brand range, compared'],
  guide: ['Step-by-step buying advice', 'Plain-English guidance before you buy'],
  comparison: ['Side-by-side model comparison', 'Head-to-head, spec by spec'],
  feature: ['Picks built around one feature', 'Feature-first shortlist, priced'],
  specialty: ['Specialty picks, compared', 'The niche list with live prices'],
  filter: ['Find the right filter fast', 'Filter matches for this family'],
}

const SLUG_PHRASINGS: Array<{ prefix: string; phrasings: [string, string] }> = [
  { prefix: 'tools/', phrasings: ['Free Fridge.com calculator', 'Work it out in seconds, free'] },
  {
    prefix: 'collections/',
    phrasings: ['Live multi-retailer price comparison', 'Prices from every tracked retailer'],
  },
  { prefix: 'blogs/', phrasings: ['Expert guide from Fridge.com', 'Researched and written in-house'] },
]

/**
 * The label for one card. `slot` is the card's index in its grid; adjacent
 * slots of the same kind alternate phrasings, which is what makes a
 * same-type run read as variety instead of a stuck template.
 */
export function hubCardDescription(
  guide: { slug: string; hub_type?: string | null },
  slot: number,
): string {
  const pick = (pair: [string, string]) => pair[slot % 2]

  for (const { prefix, phrasings } of SLUG_PHRASINGS) {
    if (guide.slug.startsWith(prefix)) return pick(phrasings)
  }

  const typed = guide.hub_type ? TYPE_PHRASINGS[guide.hub_type] : undefined
  if (typed) return pick(typed)

  // Unknown nature: say something true of every hub_pages row rather than
  // asserting a class ("Category hub") the destination may not be.
  return pick(['Explore the full breakdown', 'The deeper dive, with live data'])
}
